/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import {
  type IMentionable,
  filterMentionables,
  mentionQueryAt,
} from 'uplisting-frontend/utils/team-mentions';

interface IArgs {
  value: string;
  disabled?: boolean;
  authorName?: string | null;
  mentionables?: IMentionable[];
  onInput(value: string): void;
  onSubmit(): void;
  onMention?(mention: IMentionable): void;
}

interface TeamComposerSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

const PICK_KEYS = ['Enter', 'Tab'];

export default class UiTeamComposerComponent extends Component<TeamComposerSignature> {
  @tracked mentionQuery?: string;
  @tracked mentionStart = 0;
  @tracked activeSuggestion = 0;

  private input?: HTMLTextAreaElement;

  get hasContent(): boolean {
    return this.args.value.trim().length > 0;
  }

  get isSubmitDisabled(): boolean {
    return Boolean(this.args.disabled) || !this.hasContent;
  }

  get suggestions(): IMentionable[] {
    if (this.mentionQuery === undefined) {
      return [];
    }

    return filterMentionables(this.args.mentionables ?? [], this.mentionQuery);
  }

  get isSuggesting(): boolean {
    return this.suggestions.length > 0;
  }

  @action
  handleInput(event: Event): void {
    const input = event.target as HTMLTextAreaElement;

    this.input = input;
    this.args.onInput(input.value);
    this.refreshMentionQuery(input);
  }

  @action
  handleKeyDown(event: KeyboardEvent): void {
    if (!this.isSuggesting) {
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const count = this.suggestions.length;

      this.activeSuggestion = (this.activeSuggestion + step + count) % count;
    } else if (
      PICK_KEYS.includes(event.key) &&
      !event.metaKey &&
      !event.ctrlKey
    ) {
      event.preventDefault();
      const mention = this.suggestions[this.activeSuggestion];

      if (mention) {
        this.pickMention(mention);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeSuggestions();
    }
  }

  @action
  handleBlur(): void {
    window.setTimeout(() => this.closeSuggestions(), 150);
  }

  @action
  pickMention(mention: IMentionable): void {
    if (this.mentionQuery === undefined) {
      return;
    }

    const { value } = this.args;
    const end = this.mentionStart + 1 + this.mentionQuery.length;
    const inserted = `@${mention.name} `;
    const next = `${value.slice(0, this.mentionStart)}${inserted}${value.slice(end)}`;

    this.args.onInput(next);
    this.args.onMention?.(mention);
    this.closeSuggestions();

    const caret = this.mentionStart + inserted.length;
    const { input } = this;

    if (input) {
      window.requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(caret, caret);
      });
    }
  }

  private refreshMentionQuery(input: HTMLTextAreaElement): void {
    const found = mentionQueryAt(
      input.value,
      input.selectionStart ?? input.value.length,
    );

    if (found && (this.args.mentionables ?? []).length > 0) {
      if (found.query !== this.mentionQuery) {
        this.activeSuggestion = 0;
      }

      this.mentionQuery = found.query;
      this.mentionStart = found.start;
    } else {
      this.closeSuggestions();
    }
  }

  private closeSuggestions(): void {
    this.mentionQuery = undefined;
    this.activeSuggestion = 0;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Team::Composer': typeof UiTeamComposerComponent;
  }
}
